import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useReducer,
} from 'react';
import { Location, useBlocker } from 'react-router-dom';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';

interface UseRouteGuardApi {
  isRouteGuardModalOpen: boolean;
  isRouterBlocked: boolean;
  nextLocation?: Location;
  blockRouter: () => void;
  unblockRouter: () => void;
  closeModal: () => void;
  handleLeaveWithoutSaving: () => void;
  handleContinueEditing: () => void;
}

interface State {
  isRouteGuardModalOpen: boolean;
  isRouterBlocked: boolean;
}

interface RouteGuardContextType {
  isRouteGuardModalOpen: boolean;
  isRouterBlocked: boolean;
  nextLocation?: Location;
  block: () => void;
  unblock: () => void;
  closeModal: () => void;
  handleLeaveWithoutSaving: () => void;
  handleContinueEditing: () => void;
}

export enum ROUTE_GUARD_ACTION {
  LEAVE_WITHOUT_SAVING = 'LEAVE_WITHOUT_SAVING',
  SAVE_AND_LEAVE = 'SAVE_AND_LEAVE',
  CONTINUE_EDITING = 'CONTINUE_EDITING',
  RESET_TO_GUARANTEE_ONE_EVENT_PROPOGATION = 'RESET_TO_GUARANTEE_ONE_EVENT_PROPOGATION',
}
/**
 * The RouteGuardProvider should be used as a RouteGuard that prevents user from leaving page and gives user options to resolve staying/leaving page
 */
const RouteGuardContext = React.createContext<RouteGuardContextType>({} as any);

const getInitState = (): State => ({
  isRouteGuardModalOpen: false,
  isRouterBlocked: false,
});

// PROVIDER
function RouteGuardProvider(props) {
  const [state, setState] = useState(getInitState());
  const [, { closeModal: close }] = useModal();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      state.isRouterBlocked && currentLocation.pathname !== nextLocation.pathname
  );
  const nextLocation = blocker.state === 'blocked' ? blocker.location : undefined;

  // keep a live reference so callbacks below never act on a stale blocker snapshot
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  const handleLeaveWithoutSaving = useCallback(() => {
    if (blockerRef.current.state === 'blocked') {
      blockerRef.current.proceed?.();
    }
    blockerRef.current.reset?.();
  }, []);

  const handleContinueEditing = useCallback(() => {
    close();
    blockerRef.current.reset?.();
    setState((prevState) => ({ ...prevState, isRouteGuardModalOpen: false }));
  }, [close]);

  const block = useCallback(() => {
    setState((prevState) => ({ ...prevState, isRouterBlocked: true }));
  }, []);

  const unblock = useCallback(() => {
    blockerRef.current.reset?.();
    setState(getInitState()); // not blocked -> clear state
  }, []);

  const closeModal = useCallback(() => {
    close();
    setState((prevState) => ({ ...prevState, isRouteGuardModalOpen: false }));
  }, [close]);

  useEffect(() => {
    return () => {
      if (blockerRef.current.state === 'blocked') {
        blockerRef.current.proceed?.();
      }
      blockerRef.current.reset?.();
    };
  }, []);

  useEffect(() => {
    const isBlocked = blocker.state === 'blocked';
    if (!isBlocked) {
      setState(getInitState()); // not blocked -> clear state
      return;
    }
    setState((prevState) => ({ ...prevState, isRouteGuardModalOpen: true }));
  }, [blocker.state]);

  const contextValue = useMemo(
    () => ({
      isRouteGuardModalOpen: state.isRouteGuardModalOpen,
      isRouterBlocked: state.isRouterBlocked,
      nextLocation,
      block,
      unblock,
      closeModal,
      handleLeaveWithoutSaving,
      handleContinueEditing,
    }),
    [
      state.isRouteGuardModalOpen,
      state.isRouterBlocked,
      nextLocation,
      block,
      unblock,
      closeModal,
      handleLeaveWithoutSaving,
      handleContinueEditing,
    ]
  );

  return (
    <RouteGuardContext.Provider value={contextValue}>{props.children}</RouteGuardContext.Provider>
  );
}

// HOOK
function useRouteGuard(): UseRouteGuardApi {
  const reducer = (state, action) => {
    switch (action.type) {
      case ROUTE_GUARD_ACTION.LEAVE_WITHOUT_SAVING:
        return { type: ROUTE_GUARD_ACTION.LEAVE_WITHOUT_SAVING };
      case ROUTE_GUARD_ACTION.CONTINUE_EDITING:
        return { type: ROUTE_GUARD_ACTION.CONTINUE_EDITING };
      case ROUTE_GUARD_ACTION.SAVE_AND_LEAVE:
        return { type: ROUTE_GUARD_ACTION.SAVE_AND_LEAVE };
      case ROUTE_GUARD_ACTION.RESET_TO_GUARANTEE_ONE_EVENT_PROPOGATION:
      default:
        return null;
    }
  };
  const [state, dispatch] = useReducer(reducer, null);
  const {
    isRouterBlocked,
    isRouteGuardModalOpen,
    nextLocation,
    block,
    unblock,
    closeModal,
    handleLeaveWithoutSaving,
    handleContinueEditing,
  } = useContext(RouteGuardContext);

  if (state) {
    dispatch({ type: ROUTE_GUARD_ACTION.RESET_TO_GUARANTEE_ONE_EVENT_PROPOGATION });
  }

  return {
    isRouteGuardModalOpen,
    isRouterBlocked,
    nextLocation,
    closeModal,
    blockRouter: block,
    unblockRouter: unblock,
    handleLeaveWithoutSaving,
    handleContinueEditing,
  };
}

export { RouteGuardProvider, useRouteGuard };
